import OfferPageStateProvider from 'contexts/OfferPage/offerPageStateProvider'
import type { OfferPageState, OfferPageDispatchAction } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions, generateOfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageReducer from 'hooks/OfferPage/useOfferPageReducer'
import { useAppSelector } from 'hooks/reduxHooks'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import type { ReducerMiddleware } from 'lib/redux/reducerUtils'
import React, { useEffect, useState } from 'react'
import { getInitialLeOfferPageState } from 'selectors/offerPage/initialOfferPageStateSelectors'
import accommodationMobileFilterMiddleware from 'components/OfferPage/Accommodation/AccommodationMobileFilterMiddleware'
import { getNextFilterWithCapacityCheckInMiddleware } from '../utils'
import { useSyncHotelTripItem } from 'tripPlanner/hooks/bookmarks/useSyncHotelTripItem'
import useMountedEffect from 'hooks/useMountedEffect'
import { offerPageDurationChangeEvent, offerPageGuestSelectionEvent, offerPageHotLeadEvent } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'
import { useSaveHotLead } from 'hooks/Recommendation/useSaveHotLead'
import AlwaysOnContent from './AlwaysOnContent'
import type { InViewHookResponse } from 'react-intersection-observer'

const mobileVariantMiddlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [accommodationMobileFilterMiddleware]
const defaultMiddlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [getNextFilterWithCapacityCheckInMiddleware]
const defaultRooms = [generateOfferPageRoom()]

interface Props {
  offer: App.HotelOffer
  shouldUseLereStickyFooter: boolean
  lereYmalStickyFooterShowTrigger: InViewHookResponse
}

function AlwaysOnContainer(props: Props) {
  const { offer, shouldUseLereStickyFooter, lereYmalStickyFooterShowTrigger } = props
  const [middlewares, setMiddlewares] = useState(defaultMiddlewares)
  const defaults = useAppSelector(state => getInitialLeOfferPageState(state, offer))

  const isMobileDevice = useIsMobileDevice()
  const [state, stateDispatch] = useOfferPageReducer({
    ...defaults,
    duration: defaults.duration ?? offer.lowestPricePackage?.duration,
    offer,
  }, middlewares)

  useSyncHotelTripItem(state, offer)

  useEffect(() => {
    if (isMobileDevice) {
      setMiddlewares(mobileVariantMiddlewares)
    } else {
      setMiddlewares(defaultMiddlewares)
    }
  }, [isMobileDevice])

  useMountedEffect(() => {
    // the first loaded offer from an SSR is a summary. We then fetch the full version
    // This ensures the offer page state has our latest version
    stateDispatch({ type: OfferPageActions.setOffer, offer })
  }, [offer])

  useMountedEffect(() => {
    Analytics.trackEvent(offerPageDurationChangeEvent(offer, state))
  }, [state.duration])

  useMountedEffect(() => {
    if (state.rooms) {
      Analytics.trackEvent(offerPageGuestSelectionEvent(offer, state))
    }
  }, [state.rooms])

  const hotLead = useSaveHotLead({
    offerId: offer.id,
    offerType: offer.type,
    checkInDate: state.checkInDate,
    duration: state.duration,
    occupancy: state.rooms,
    defaultOccupancy: defaultRooms,
  })

  useEffect(() => {
    if (hotLead?.checkIn && hotLead.checkOut) {
      Analytics.trackEvent(offerPageHotLeadEvent(offer, state))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotLead])

  return <OfferPageStateProvider state={state} dispatch={stateDispatch}>
    <AlwaysOnContent
      offer={offer}
      shouldUseLereStickyFooter={shouldUseLereStickyFooter}
      lereYmalStickyFooterShowTrigger={lereYmalStickyFooterShowTrigger}
    />
  </OfferPageStateProvider>
}

export default React.memo(AlwaysOnContainer)
